import { createApi } from "@reduxjs/toolkit/query/react";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import {
	baseQueryWithRedirect,
	encodePostRequest,
	UntransformedResponse,
	APIErrorResponse
} from "features/api/utils/apiUtils.ts";
import type { components } from "types/declarations/api";

type CampaignAllocationResponse = components["schemas"]["CampaignAllocation"];

export type CampaignAllocation = CampaignAllocationResponse & {
	selectionKey?: string;
	campaignId: string;
	campaignName: string;
	quantity: number;
	jobName: string;
	startDate: string;
	expirationDate: string;
};

export interface CreateCampaignOrderRequest {
	accountId: string;
	billToId: string;
	address: unknown;
	poNumber: string;
	jobName: string;
	selections: Array<{ campaignId: string; allocationId: string }>;
}

type ApiResponse<T> = { data?: T; errors?: Array<{ message?: string }> };

export const campaignApiSlice = createApi({
	reducerPath: "campaignApi",
	baseQuery: baseQueryWithRedirect(baseURL),
	tagTypes: ["CampaignAllocations"],
	endpoints: (builder) => ({
		getCampaignAllocations: builder.query<CampaignAllocation[], void>({
			query: () => endpoints.campaigns.getAllocations(),
			transformResponse: (response: ApiResponse<CampaignAllocationResponse[]>) =>
				(response.data ?? []) as CampaignAllocation[],
			providesTags: ["CampaignAllocations"]
		}),
		createCampaignOrders: builder.mutation<string[], CreateCampaignOrderRequest>({
			query: (request) => ({
				url: endpoints.orders.createCampaignOrders(),
				method: "POST",
				headers: { "Content-type": "application/json; charset=UTF-8" },
				body: encodePostRequest(request)
			}),
			transformResponse: (response: ApiResponse<string[]>) => response.data ?? [],
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error.data?.errors,
				status: error.status
			}),
			invalidatesTags: ["CampaignAllocations"]
		})
	})
});

export const { useGetCampaignAllocationsQuery, useCreateCampaignOrdersMutation } = campaignApiSlice;
